import { useMemo } from 'react';
import { useGetProductionDeclarationMaterialsCompleteness } from '@/services/apis/queries/production-declaration/use-get-production-declaration-materials-completeness';
import type {
  ProductionDeclarationCommodityOriginCompleteness,
  ProductionDeclarationMaterialCompleteness,
} from '@/types/core/purchase-order/production-declaration-material-completeness';

export type IncompleteMaterial = ProductionDeclarationMaterialCompleteness & {
  incompleteOrigins: ProductionDeclarationCommodityOriginCompleteness[];
  hasMissingOrigins: boolean;
};

type UseProductionDeclarationCompletenessParams = {
  productionDeclarationId: string;
  token?: string;
};

export function useProductionDeclarationCompleteness({
  productionDeclarationId,
  token,
}: UseProductionDeclarationCompletenessParams) {
  const { data: materialsCompleteness = [] } =
    useGetProductionDeclarationMaterialsCompleteness(
      productionDeclarationId,
      token,
    );

  const incompleteMaterials = useMemo(
    () =>
      materialsCompleteness
        .map((m) => {
          const incompleteOrigins = m.commodityOrigins.filter(
            (co) =>
              !(co.hasProductionPlaces && co.hasSupplyChain && co.hasDocuments),
          );
          const hasMissingOrigins = m.commodityOrigins.length === 0;
          if (!hasMissingOrigins && incompleteOrigins.length === 0) {
            return null;
          }
          return { ...m, incompleteOrigins, hasMissingOrigins };
        })
        .filter((m): m is IncompleteMaterial => m !== null),
    [materialsCompleteness],
  );

  const isComplete = incompleteMaterials.length === 0;

  return { incompleteMaterials, isComplete };
}
